import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { filter, find, findIndex, size } from 'lodash';
import { RootState } from '../store';
import {
  CashType,
  CustomerType,
  DataType,
  DiscountType,
  InovicesType,
  ItemType,
  ModifierType,
  PendingsType,
  ShiftType,
  TicketType,
} from './dataTypes';

const initialState: DataType = {
  categories: [],
  items: [],
  modifiers: [],
  discounts: [],
  shift: null,
  tickets: [],
  pendings: [],
  invoices: [],
  cashes: [],
  customers: [],
};
const dataSlice = createSlice({
  name: 'data',
  initialState,
  reducers: {
    setItems(state, action: PayloadAction<ItemType[]>) {
      state.items = action.payload;
    },
    setModifiers(state, action: PayloadAction<ModifierType[]>) {
      state.modifiers = action.payload;
    },
    setDiscounts(state, action: PayloadAction<DiscountType[]>) {
      state.discounts = action.payload;
    },
    setShift(state, action: PayloadAction<ShiftType | null>) {
      state.shift = action.payload;
    },
    setInvoices(state, action: PayloadAction<InovicesType[]>) {
      state.invoices = action.payload;
    },
    updateInvoice(state, action: PayloadAction<InovicesType>) {
      const invoiceIndex = findIndex(state.invoices, (d) => d.ID === action.payload.ID);
      if (invoiceIndex > -1 && state.invoices && state.invoices.length > 0) {
        state.invoices[invoiceIndex] = action.payload;
      }
    },
    addInvoice(state, action: PayloadAction<InovicesType>) {
      state.invoices = [action.payload, ...(state.invoices ?? [])];
      if (size(action.payload.items) > 0) {
        action.payload.items?.forEach((item) => {
          const orginalItemIdx = findIndex(state.items, (d) => d.ID == item.ID);
          if (orginalItemIdx > -1 && state.items[orginalItemIdx].stock != null) {
            state.items[orginalItemIdx] = {
              ...state.items[orginalItemIdx],
              stock: state.items[orginalItemIdx].stock - item.quantity,
            };
          }
        });
      }
    },
    setCashes(state, action: PayloadAction<CashType[]>) {
      state.cashes = action.payload;
    },
    addCash(state, action: PayloadAction<CashType>) {
      state.cashes = [action.payload, ...(state.cashes ?? [])];
    },
    setPending(state, action: PayloadAction<PendingsType>) {
      state.pendings = [...(state.pendings ?? []), action.payload];
    },
    removePending(state, action: PayloadAction<PendingsType>) {
      state.pendings = state.pendings?.filter((d) => {
        return !(d.ID == action.payload.ID && d.type == action.payload.type);
      });
    },
    addTicket(state, action: PayloadAction<TicketType>) {
      state.tickets = [...(state.tickets ?? []), action.payload];
    },
    updateTicket(state, action: PayloadAction<TicketType>) {
      if (!state.tickets) {
        state.tickets = [];
      }
      const ticketIndex = findIndex(state.tickets, (d) => d.ID === action.payload.ID);
      if (ticketIndex > -1) {
        // Update existing ticket
        state.tickets[ticketIndex] = {
          ...state.tickets[ticketIndex],
          ...action.payload,
        };
      } else {
        console.warn('Ticket not found', action.payload);
      }
    },
    removeTicket(state, action: PayloadAction<TicketType>) {
      state.tickets = state.tickets?.filter((d) => {
        return d.ID != action.payload.ID;
      });
    },
    clearTicket(state) {
      state.tickets = [];
    },
    clearPending(state) {
      state.pendings = [];
    },
    setCustomers(state, action: PayloadAction<CustomerType[]>) {
      state.customers = action.payload;
    },
    setData(state, action: PayloadAction<DataType>) {
      state.categories = action.payload.categories;
      state.items = action.payload.items;
      state.modifiers = action.payload.modifiers;
      state.discounts = action.payload.discounts;
      if (action.payload.customers) {
        state.customers = action.payload.customers;
      }
    },
  },
});

export const {
  setData,
  setItems,
  setModifiers,
  setDiscounts,
  setShift,
  setPending,
  clearPending,
  removePending,
  addTicket,
  updateTicket,
  clearTicket,
  removeTicket,
  setInvoices,
  addInvoice,
  updateInvoice,
  setCashes,
  addCash,
  setCustomers,
} = dataSlice.actions;

export const selectCategories = (state: RootState) => state.data.categories;
export const selectItems = (state: RootState) => state.data.items;
export const selectShift = (state: RootState) => state.data.shift;
export const selectPendings = (state: RootState) => state.data.pendings;
export const selectInvoices = (state: RootState) => state.data.invoices;
export const selectCashes = (state: RootState) => state.data.cashes;
export const selectTickets = (state: RootState) => state.data.tickets;

export const selectActiveTicket = createSelector([selectTickets], (tickets) =>
  find(tickets, (d) => d.status == 'ACTIVE')
);

export const selectOpenedTicket = createSelector([selectTickets], (tickets) =>
  filter(tickets, (d) => d.status == 'BACKLOG')
);

export const selectDiscounts = (state: RootState) => state.data.discounts;
export const selectModifiers = (state: RootState) => state.data.modifiers;
export const selectCustomers = (state: RootState) => state.data.customers;
export default dataSlice.reducer;
